/**
 * Time in the portal is the BUSINESS's local time, never the browser's. A business runs on its state's Australian time zone (Victoria =
 * Melbourne, Queensland = Brisbane with no daylight saving, WA = Perth, ...; see lib/timeZone.ts), and everything the portal shows or
 * works out about "today", a day's start and end, or a time of day goes through here, so a phone set to another zone, or an admin
 * looking at a business in another state, sees the same day and the same times as the business does.
 *
 * The zone in use is set once from the active business (lib/auth.tsx). Without one (the platform admin's own screens) it is Sydney.
 *
 * Calendar dates ("2026-10-05") and weekdays are zone-free arithmetic; only INSTANTS are converted.
 */

export const DEFAULT_TIME_ZONE = "Australia/Sydney";
/** Platform-level screens (invoices, licences, rate cards, VoIP usage) run on this time: billing months are Sydney months. */
export const PLATFORM_TIME_ZONE = "Australia/Sydney";

let displayTimeZone = DEFAULT_TIME_ZONE;

/** Sets the zone every helper below uses unless one is passed. Ignores an empty or unknown zone. */
export function setDisplayTimeZone(zone: string | null | undefined): void {
  if (zone && isValidTimeZone(zone)) displayTimeZone = zone;
}

export function getDisplayTimeZone(): string {
  return displayTimeZone;
}

function isValidTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-AU", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(zone: string): Intl.DateTimeFormat {
  let f = formatters.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(zone, f);
  }
  return f;
}

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
  /** 0 = Sunday */
  weekday: number;
}

/** The wall-clock reading of an instant in a zone. */
export function zonedParts(value: Date | string | number, zone: string = displayTimeZone): ZonedParts {
  const date = value instanceof Date ? value : new Date(value);
  const map: Record<string, number> = {};
  for (const p of partsFormatter(zone).formatToParts(date)) if (p.type !== "literal") map[p.type] = Number(p.value);
  const year = map.year!;
  const month = map.month!;
  const day = map.day!;
  return { year, month, day, hour: map.hour! % 24, minute: map.minute!, second: map.second!, weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay() };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "2026-10-05": the business-local calendar day an instant falls on. */
export function ymdOfInstant(value: Date | string | number, zone: string = displayTimeZone): string {
  const p = zonedParts(value, zone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** Today's business-local calendar date. */
export function todayInZone(zone: string = displayTimeZone): string {
  return ymdOfInstant(Date.now(), zone);
}

/** Minutes since local midnight of an instant (a 9:45 am start is 585). */
export function minutesOfDayInZone(value: Date | string | number, zone: string = displayTimeZone): number {
  const p = zonedParts(value, zone);
  return p.hour * 60 + p.minute;
}

/** The weekday (0 = Sunday) of a calendar date. Zone-free: a date has the same weekday everywhere. */
export function weekdayOfYmd(ymd: string): number {
  const [y = 1970, m = 1, d = 1] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** A calendar date shifted by whole days. Zone-free. */
export function addDaysYmd(ymd: string, days: number): string {
  const [y = 1970, m = 1, d = 1] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * The instant at which a zone's wall clock reads `ymd` `time` ("HH:MM" or "HH:MM:SS"). Two passes of the offset correction settle it on a
 * daylight-saving change day. A time the clock skips (2:30 am on the day clocks go forward) lands an hour later, like a calendar app; a time it
 * reads twice (clocks going back) is taken as the second, standard-time one.
 */
export function zonedWallToInstant(ymd: string, time: string, zone: string = displayTimeZone): Date {
  const [y = 1970, mo = 1, d = 1] = ymd.split("-").map(Number);
  const [h = 0, mi = 0, s = 0] = time.split(":").map(Number);
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi, s);
  const offsetAt = (instant: number) => {
    const p = zonedParts(instant, zone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(instant / 1000) * 1000;
  };
  let guess = wallAsUtc - offsetAt(wallAsUtc);
  guess = wallAsUtc - offsetAt(guess);
  return new Date(guess);
}

/** "09:45" for 585 minutes after midnight: a wall-clock time for zonedWallToInstant. */
export function wallTimeOfMinutes(minutes: number): string {
  return `${pad(Math.floor(minutes / 60) % 24)}:${pad(minutes % 60)}`;
}

/** 00:00 of a business-local calendar day, as an instant. */
export function startOfDayInZone(ymd: string, zone: string = displayTimeZone): Date {
  return zonedWallToInstant(ymd, "00:00", zone);
}

/** The start of the NEXT day: an exclusive end for "everything on this day" (a day is 23 or 25 hours long when the clocks change). */
export function endOfDayExclusive(ymd: string, zone: string = displayTimeZone): Date {
  return startOfDayInZone(addDaysYmd(ymd, 1), zone);
}
